import {
  Form, FormActionsAlign, FormHandlers, FormSubmitType,
} from '@doveit/bricks';
import React from 'react';
import { omit } from 'lodash';
import * as Yup from 'yup';
import { provinceSelectOptions } from '../../../select-options';
import JobPositionsSelect from '../../../job-position/containers/job-positions-select/JobPositionsSelect';
import { booleanStringToBool } from '../../../utils/boolean-string/booleanString';
import { jobApplicationSourceLabels } from '../../../labels/jobApplicationSourceLabels';
import { JobApplicationSource } from '../../../domain/types/jobApplicationSource';
import { asSelectOptions } from '../../../select-options/utils/asSelectOptions';
import { Department, JobApplicationType } from '../../../domain/types';
import { INPUT_PLACEHOLDERS } from '../../../constants';
import { JobApplicationFormModel } from '../../types';
import { jobApplicationTypeLabels } from '../../../labels';
import { JobPosition } from '../../../providers/api/dtos';
import { jobApplicationExperiencesRadioOptions } from '../../constants';

export interface JobApplicationFormProps {
  initialValues: Partial<JobApplicationFormModel>
  onSubmit: FormSubmitType<JobApplicationFormModel>
  innerRef?: React.MutableRefObject<FormHandlers>,
  validationSchema: Yup.AnySchema,
  hiddenFields?: Partial<Record<keyof JobApplicationFormModel, boolean>>
  disabledFields?: Partial<Record<keyof JobApplicationFormModel, boolean>>
  loading?: boolean,
  disabled?: boolean,
}

export const JOB_APPLICATION_FORM_DEFAULT_VALUES = {
  applicationType: JobApplicationType.SALES,
  jobPosition: undefined,
  source: '',
  experience: '',
  isEmployed: undefined,
  provinces: [],
  hasLicense: undefined,
  licenseId: '',
};

const JobApplicationForm: React.FC<JobApplicationFormProps> = ({
  initialValues,
  hiddenFields,
  disabledFields,
  innerRef,
  ...rest
}) => {
  const mergedInitialValues = React.useMemo(() => ({
    ...JOB_APPLICATION_FORM_DEFAULT_VALUES,
    ...initialValues,
  }), [initialValues]);

  const jobApplicationsSourceOptions = React.useMemo(() => {
    const filtered = omit(jobApplicationSourceLabels, JobApplicationSource.REFERRAL_AGENZIA);
    const sourceOptions = asSelectOptions(filtered, false).sort((a, b) => a.label.localeCompare(b.label, 'it'));
    return [{ label: 'Seleziona una sorgente', value: '', disabled: true }, ...sourceOptions];
  }, []);

  return (
    <Form
      initialValues={mergedInitialValues}
      innerRef={innerRef}
      {...rest}
    >
      {({ values, setFieldValue }) => {
        const onRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
          const newJobPosition = e.target.value as any as JobPosition;
          setFieldValue('jobPosition', newJobPosition);
        };

        return (
          <>
            {!hiddenFields?.applicationType && (
              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.Select
                  label="Tipologia"
                  name="applicationType"
                  required
                  aria-label="Campo per specificare la tipologia"
                  options={Object.values(JobApplicationType).map((value) => ({ value, label: jobApplicationTypeLabels[value] }))}
                  disabled={disabledFields?.applicationType}
                />
              </Form.Item>
            )}
            {!hiddenFields?.jobPosition && (
              <Form.Item size={{ MD: 1 / 2 }}>
                <JobPositionsSelect
                  label="Posizione"
                  name="jobPosition"
                  value={values.jobPosition}
                  required
                  aria-label="Campo per specificare il ruolo"
                  filterJobPositions={(jobPosition) => (values.applicationType === JobApplicationType.SALES ? jobPosition.department === Department.SALES : jobPosition.department !== Department.SALES)}
                  disabled={disabledFields?.jobPosition}
                  onChange={onRoleChange}
                />
              </Form.Item>
            )}
            {!hiddenFields?.isEmployed && (
              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.YesOrNotButtons
                  label="Attualmente lavora?"
                  name="isEmployed"
                  aria-label="Campo per specificare se il candidato attualmente lavora"
                  disabled={disabledFields?.isEmployed}
                />
              </Form.Item>
            )}
            {!hiddenFields?.source && (
              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.Select
                  label="Sorgente"
                  name="source"
                  required
                  aria-label="Campo per specificare la sorgente"
                  options={jobApplicationsSourceOptions}
                  disabled={disabledFields?.source}
                />
              </Form.Item>
            )}
            {!hiddenFields?.experience && (
              <Form.Item>
                <Form.RadioButtonGroup
                  name="experience"
                  label="Livello di esperienza"
                  itemSize={{ MD: 1 / 2, LG: 1 / 4 }}
                  items={jobApplicationExperiencesRadioOptions}
                  aria-label="Campo per specificare l'esperienza"
                  disabled={disabledFields?.experience}
                />
              </Form.Item>
            )}

            {values.applicationType === JobApplicationType.SALES && (
              <Form.Fieldset legend="Dettagli">
                {!hiddenFields?.provinces && (
                  <Form.Item>
                    <Form.MultiSelect
                      name="provinces"
                      label="Luogo"
                      options={provinceSelectOptions}
                      required
                      aria-label="Campo per specificare le province coperte"
                      disabled={disabledFields?.provinces}
                    />
                  </Form.Item>
                )}
                {!hiddenFields?.hasLicense && (
                  <Form.Item size={{ MD: 1 / 2 }}>
                    <Form.YesOrNotButtons
                      label="Ha il patentino?"
                      name="hasLicense"
                      required
                      aria-label="Campo per specificare se il candidato ha il patentino"
                      disabled={disabledFields?.hasLicense}
                    />
                  </Form.Item>
                )}
                {!hiddenFields?.licenseId && booleanStringToBool(values.hasLicense) && (
                  <Form.Item size={{ MD: 1 / 2 }}>
                    <Form.Input
                      label="Codice patentino"
                      placeholder={INPUT_PLACEHOLDERS.JOB_APPLICATION_LICENSE_ID}
                      name="licenseId"
                      aria-label="Campo per specificare il codice del patentino"
                      disabled={disabledFields?.licenseId}
                    />
                  </Form.Item>
                )}
              </Form.Fieldset>
            )}

            {!innerRef && (
              <Form.Actions align={FormActionsAlign.RIGHT}>
                <Form.Submit label="Salva" />
              </Form.Actions>
            )}
          </>
        );
      }}
    </Form>
  );
};

export default JobApplicationForm;
